import { keymap } from "@codemirror/view";

import type { Extension } from "@codemirror/state";
import type { Command, KeyBinding } from "@codemirror/view";

const LINE_COMMENT_MARKER = "// ";

/**
 * Toggles a `//` line-comment prefix on every non-blank line touched by the
 * current selection(s).
 *
 * `@codemirror/lang-json`'s grammar has no comment tokens of its own
 * (standard JSON has none), so CodeMirror's built-in `toggleLineComment`
 * command — which looks up a language's declared `commentTokens` — is a
 * no-op for this editor. This reimplements the same familiar UX directly,
 * specifically for `//`, matching the comments a comment-tolerant JSON
 * parser (e.g. `jsonc-parser`) can strip back out before validating/sending.
 */
export const toggleJsonLineComment: Command = (view) => {
    const { state } = view;
    if (state.readOnly) {
        return false;
    }

    const lineNumbers = new Set<number>();
    for (const range of state.selection.ranges) {
        const startLine = state.doc.lineAt(range.from).number;
        const endLine = state.doc.lineAt(range.to).number;
        for (let lineNumber = startLine; lineNumber <= endLine; lineNumber += 1) {
            lineNumbers.add(lineNumber);
        }
    }

    const lines = [...lineNumbers].sort((a, b) => a - b).map((lineNumber) => state.doc.line(lineNumber));
    const commentableLines = lines.filter((line) => line.text.trim().length > 0);
    if (commentableLines.length === 0) {
        return false;
    }

    const allCommented = commentableLines.every((line) => line.text.trimStart().startsWith("//"));

    const changes = commentableLines.map((line) => {
        const indent = line.text.length - line.text.trimStart().length;
        if (!allCommented) {
            return { from: line.from + indent, insert: LINE_COMMENT_MARKER };
        }
        const withoutIndent = line.text.slice(indent);
        const markerLength = withoutIndent.startsWith(LINE_COMMENT_MARKER) ? LINE_COMMENT_MARKER.length : 2;
        return { from: line.from + indent, to: line.from + indent + markerLength };
    });

    view.dispatch(state.update({ changes }));
    return true;
};

const COMMENT_KEY_BINDING: KeyBinding = { key: "Mod-/", run: toggleJsonLineComment };

/** Keymap extension binding Cmd-/ / Ctrl-/ to {@link toggleJsonLineComment}. */
export const jsonLineCommentKeymap: Extension = keymap.of([COMMENT_KEY_BINDING]);
